<template>
  <div id="scholarship-issued-list">
    <v-dialog v-model="previewRecordPrompt" persistent max-width="90%">
      <student-scholarship-preview
        pageDescription="Student scholarship preview"
        :previewRecordPrompt="previewRecordPrompt"
        @hidePreviewRecordPrompt="hidePreviewRecordPrompt()"
        :recordData="selectedData"
        v-if="previewRecordPrompt"
      ></student-scholarship-preview>
    </v-dialog>

    <!-- Header Card -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <v-container fluid class="header-container">
          <v-row align="center" class="fill-height pa-4">
            <v-col sm="7" xs="12" class="header-content d-flex align-center">
              <div class="header-icon-box">
                <v-icon size="48" color="white">mdi-file-document</v-icon>
              </div>
              <div class="header-title-bar">
                <h1 class="header-title">{{ PageTitle }}</h1>
                <p class="header-subtitle">{{ YearName }}</p>
              </div>
            </v-col>
            <v-col sm="5" xs="12" class="d-flex justify-end align-center gap-3">
              <v-btn
                @click.prevent="pageData"
                icon
                rounded
                x-large
                class="action-btn"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-card>

    <!-- Description Card -->
    <v-card elevation="4" class="mb-6 info-card">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="auto">
            <v-icon size="32" color="primary">mdi-information-outline</v-icon>
          </v-col>
          <v-col>
            <h4 class="text-subtitle-1 font-weight-600">
              {{ PageDescription }}
            </h4>
            <p class="text-body2 mb-0">
              Manage and view all scholarship issued records
            </p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Filters Card -->
    <v-card elevation="4" class="filters-card mb-6">
      <v-card-text class="pa-4">
        <v-row align="end" class="mb-0">
          <!-- Year Selection -->
          <v-col cols="12" sm="6" md="3" lg="3">
            <label class="field-label required-field">Year</label>
            <v-autocomplete
              :reduce="(option) => option.value"
              :items="JciYearCodeOptions"
              :rules="JciYearCodeRules"
              :loading="JciYearCodeOptionsLoading"
              v-model="JciYearCode"
              dense
              clearable
              required
              outlined
              class="field-input"
              prepend-inner-icon="mdi-calendar"
            ></v-autocomplete>
          </v-col>

          <!-- Download Button -->
          <v-col
            cols="12"
            sm="6"
            md="3"
            lg="3"
            v-if="rows.length > 0 && DownloadFlag == 1"
            class="d-flex align-end"
          >
            <json-excel
              :data="rows"
              :fields="ExcelFields"
              worksheet="Scholarship Records"
              :name="ExcelFileName"
              class="w-100"
            >
              <v-btn color="success" block large class="font-weight-700">
                <v-icon left>mdi-download</v-icon>
                Download
              </v-btn>
            </json-excel>
          </v-col>

          <!-- Bulk Upload Button -->
          <v-col cols="12" sm="6" md="3" lg="1" class="d-flex align-end">
            <v-btn
              v-if="BulkUploadPermissionFlag"
              color="info"
              block
              large
              to="/student-scholarship/scholarship-issued/bulk-upload"
              class="font-weight-700"
              @click="bulkUpload"
            >
              <v-icon left>mdi-cloud-upload</v-icon>
              Bulk Upload
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Data Table Card -->
    <v-card elevation="4" class="table-card" v-if="rows.length > 0">
      <v-card-title class="card-header">
        <v-icon class="mr-3">mdi-table</v-icon>
        <span>Scholarship Records ({{ rows.length }})</span>
      </v-card-title>
      <v-card-text class="pa-0">
        <div class="table-wrapper">
          <v-data-table
            class="professional-table"
            v-model="selected"
            :headers="tableColumns1"
            :items="rows"
            :items-per-page="50"
            :search="search"
            :single-select="false"
            :show-select="false"
            :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, 50] }"
          >
            <template v-slot:top>
              <v-toolbar flat class="table-toolbar">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search records..."
                  single-line
                  hide-details
                  class="table-search"
                ></v-text-field>
              </v-toolbar>
            </template>
            <br />
            <template v-slot:[`item.StudentApprovalStatusTxt`]="{ item }">
              <v-chip
                :text-color="item.StudentApprovalStatusTxt"
                :color="item.StudentApprovalStatusColor"
                class="font-weight-600"
                small
              >
                <v-icon left small>mdi-check-circle</v-icon>
                {{ item.StudentApprovalStatusTxt }}
              </v-chip>
            </template>

            <template v-slot:[`item.ScholarshipStatusTxt`]="{ item }">
              <v-chip
                :text-color="item.ScholarshipStatusTxt"
                :color="item.ScholarshipStatusColor"
                class="font-weight-600"
                small
              >
                <v-icon left small>mdi-receipt</v-icon>
                {{ item.ScholarshipStatusTxt }}
              </v-chip>
            </template>

            <template v-slot:[`item.PhotoPath`]="{ item }">
              <div class="photo-cell">
                <img :src="item.PhotoPath" class="student-photo" />
              </div>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <div class="action-buttons">
                <v-tooltip bottom v-if="item.PreviewFlag">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="previewData(item)"
                      v-bind="attrs"
                      v-on="on"
                      class="mx-1"
                    >
                      <v-icon small>mdi-file-search</v-icon>
                    </v-btn>
                  </template>
                  <span>Preview</span>
                </v-tooltip>

                <v-tooltip bottom v-if="item.EditFlag">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="warning"
                      @click="editData(item)"
                      v-bind="attrs"
                      v-on="on"
                      class="mx-1"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>

                <v-tooltip bottom v-if="item.DeleteFlag">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="error"
                      @click="deleteConfirmAlert(item)"
                      v-bind="attrs"
                      v-on="on"
                      class="mx-1"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>

    <!-- No Data Card -->
    <v-card
      elevation="4"
      class="empty-card"
      v-if="rows.length === 0 && !SearchFlag"
    >
      <v-card-text class="pa-8 text-center">
        <v-icon size="64" color="primary" class="mb-4" style="opacity: 0.5">
          mdi-inbox-outline
        </v-icon>
        <h3 class="text-h6 text-secondary mb-2">No Records Found</h3>
        <p class="text-body2">
          Select a year to view scholarship issued records
        </p>
      </v-card-text>
    </v-card>
  </div>
</template>
  
    <script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import StudentScholarshipPreview from "@/view/pages/erp/student-scholarship/StudentScholarshipPreview.vue";
import Swal from "sweetalert2";

export default {
  components: { StudentScholarshipPreview },

  mixins: [common],
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SearchButtonFlag: false,
      SubmitFlag: false,
      SpeedDialFlag: false,
      rows: [],
      tableColumns1: [],
      tableOptions: [],
      selected: [],
      search: "",

      DownloadFlag: 0,
      ExcelFileName: "",
      ExcelFields: "",
      BulkUploadPermissionFlag: "",
      recordToEdit: "",
      record: {},
      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,
      editPageDescription: "Assign LOM President for JCI India",

      LomDataPageTitle: "",
      LomDataPreviewFlag: false,
      LomDataParams: {},

      selectedData: {},
      tableData1: {},

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      zone: {},
      lom: {},

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  mounted() {
    /*
        this.$store.dispatch(SET_BREADCRUMB, [
          { title: "Vuetify", route: "alerts" },
          { title: "Form Inputs & Control", route: "autocompletes" },
          { title: "Fileinptus" },
        ]);
        */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        this.pageData();
      }
    },
    JciYearCode: function () {
      console.log("watch JciYearCode");
      var JciYearCode = this.JciYearCode;
      console.log({ JciYearCode });
      if (JciYearCode > 0) {
        this.pageData();
      } else {
        this.rows = [];
      }
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.LoadingFlag = false;
      this.JciYearCodeOptionsLoading = false;
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "student_scholarship",
        Action: "list_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getJciYearCodeOptions();
      this.searchForm();
    },
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    deleteConfirmAlert(tr) {
      console.log("deleteConfirmAlert called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var htmlTxt = "";
        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: "Delete",
          confirmButtonColor: "#d33",
          cancelButtonText: "Cancel",
          cancelButtonColor: "#b2a496",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.deleteData(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        if (n1 == 0) {
          message += "Kindly select one record to delete. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      this.searchForm();
    },
    getJciYearCodeOptions() {
      this.LoadingFlag = true;
      this.JciYearCodeOptionsLoading = true;
      console.log("getJciYearCodeOptions called");
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 2,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    searchForm() {
      console.log("searchForm is called");

      var YearCode = this.JciYearCode;
      console.log({ YearCode });

      if (YearCode > 0) {
        this.SearchFlag = true;
        this.rows = [];

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/student/scholarship-issued/lists";
        var upload = {
          UserInterface: 1,
          Year: this.JciYearCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = [];
        var flag = 0;
        var table_data = {};
        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SearchFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.TableContent;
            console.log({ records });

            thisIns.tableColumns1 = records.TableHeader;
            thisIns.tableOptions = records.TableOptions;
            thisIns.table_data = records.TableData;
            thisIns.BulkUploadPermissionFlag = records.BulkUploadPermissionFlag;
            console.log(
              "BulkUploadPermissionFlag:",
              thisIns.BulkUploadPermissionFlag
            );

            if (flag == 1) {
              thisIns.rows = records.TableData;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SearchFlag = false;
          });
      } else {
        var message = "Kindly fill the required fields";
        this.toast("error", message);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
    <style lang="scss">
// Color Palette - JCI Brand Colors
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-90-black: #414042;
$text-off-black: #0a0f29;
$text-white: #ffffff;
$success-color: #4caf50;
$warning-color: #ff9800;
$error-color: #f44336;

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
}

#scholarship-issued-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 30px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: float 8s ease-in-out infinite;
    }
  }

  .header-backdrop-effect {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
  }

  .header-container {
    padding: 20px 0 !important;
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 20px;
  }

  .header-title-bar {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
    margin: 0;
  }

  .header-subtitle {
    font-size: 14px;
    opacity: 0.95;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.3px;
    color: rgba($text-white, 0.9);
    font-weight: 500;
    margin: 0;
  }

  .action-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

    &:hover {
      transform: translateY(-6px);
      background: rgba(255, 255, 255, 0.25) !important;
      border-color: rgba(255, 255, 255, 0.5) !important;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3) !important;
    }
  }

  .gap-3 {
    gap: 1rem !important;
  }

  // ============================================
  // INFO CARD
  // ============================================
  .info-card {
    border-radius: 16px !important;
    border: 2px solid rgba(58, 103, 177, 0.15) !important;
    box-shadow: 0 12px 40px rgba(58, 103, 177, 0.12),
      0 0 30px rgba(0, 151, 215, 0.08) !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    animation: slideInUp 0.6s ease-out;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 16px 50px rgba(58, 103, 177, 0.15),
        0 0 40px rgba(0, 151, 215, 0.12) !important;
    }
  }

  // ============================================
  // FILTERS CARD
  // ============================================
  .filters-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 12px 32px rgba(58, 103, 177, 0.12) !important;
    border: 2px solid rgba(0, 151, 215, 0.1) !important;
    animation: slideInUp 0.6s ease-out 0.1s backwards;
    transition: all 0.3s ease;

    .field-label {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: $navy;
      display: block;
      margin-bottom: 8px;

      &.required-field::after {
        content: "*";
        color: $error-color;
        margin-left: 4px;
      }
    }

    .field-input {
      transition: all 0.3s ease;

      &::v-deep {
        .v-input__control .v-input__slot {
          border-color: rgba(0, 151, 215, 0.2) !important;
        }

        &.v-input--is-focused {
          .v-input__slot {
            border-color: $aqua !important;
            box-shadow: 0 0 0 2px rgba(0, 151, 215, 0.1);
          }
        }
      }
    }

    .v-btn {
      border-radius: 8px !important;
      font-weight: 700 !important;
      text-transform: uppercase !important;
      letter-spacing: 0.5px !important;
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1) !important;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
      }
    }
  }

  // ============================================
  // TABLE CARD
  // ============================================
  .table-card {
    border-radius: 16px !important;
    background: #ffffff !important;
    box-shadow: 0 12px 32px rgba(58, 103, 177, 0.12) !important;
    border: 2px solid rgba(0, 151, 215, 0.1) !important;
    animation: slideInUp 0.6s ease-out 0.15s backwards;
    overflow: hidden;

    .card-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 18px;
      background: linear-gradient(135deg, $gold 0%, darken($gold, 15%) 100%);
      border-radius: 14px 14px 0 0;
      position: relative;
      overflow: hidden;
      z-index: 1;

      &::before {
        content: "";
        position: absolute;
        top: -50%;
        right: -50%;
        width: 200%;
        height: 200%;
        background: radial-gradient(
          circle,
          rgba(255, 255, 255, 0.1) 0%,
          transparent 70%
        );
        animation: float 6s ease-in-out infinite;
      }

      span {
        color: white;
        font-weight: 700;
        font-size: 16px;
        position: relative;
        z-index: 2;
      }

      .v-icon {
        color: white;
        position: relative;
        z-index: 2;
      }
    }

    .table-wrapper {
      overflow-x: auto;
    }

    .table-toolbar {
      background: linear-gradient(
        135deg,
        rgba(58, 103, 177, 0.05) 0%,
        rgba(0, 151, 215, 0.05) 100%
      ) !important;
      border-bottom: 2px solid rgba(0, 151, 215, 0.1);
      padding: 12px 16px !important;

      .table-search {
        max-width: 400px;

        &::v-deep {
          .v-input__control .v-input__slot {
            background: white !important;
            border: 2px solid rgba(0, 151, 215, 0.2) !important;
            border-radius: 8px;
          }
        }
      }
    }
  }

  .professional-table {
    &::v-deep {
      thead tr {
        background: linear-gradient(
          135deg,
          rgba(58, 103, 177, 0.08) 0%,
          rgba(0, 151, 215, 0.08) 100%
        ) !important;

        th {
          font-weight: 700 !important;
          color: $navy !important;
          font-size: 12px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          border-bottom: 2px solid rgba(0, 151, 215, 0.15) !important;
          padding: 16px 12px !important;
        }
      }

      tbody tr {
        transition: all 0.3s ease;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;

        &:hover {
          background: linear-gradient(
            135deg,
            rgba(0, 151, 215, 0.05) 0%,
            rgba(58, 103, 177, 0.05) 100%
          ) !important;
          box-shadow: inset 0 0 10px rgba(0, 151, 215, 0.08);
        }

        td {
          padding: 14px 12px !important;
          color: $text-90-black;
          font-size: 13px;
        }
      }
    }
  }

  .photo-cell {
    display: flex;
    justify-content: center;
    align-items: center;

    .student-photo {
      width: 50px;
      height: 50px;
      border-radius: 8px;
      border: 2px solid rgba(0, 151, 215, 0.2);
      object-fit: cover;
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.15) rotate(-2deg);
        box-shadow: 0 8px 24px rgba(0, 151, 215, 0.25);
      }
    }
  }

  .action-buttons {
    display: flex;
    justify-content: center;
    gap: 6px;

    .v-btn {
      transition: all 0.3s ease;
      background: linear-gradient(
        135deg,
        rgba(58, 103, 177, 0.1) 0%,
        rgba(0, 151, 215, 0.1) 100%
      ) !important;
      border-radius: 6px;

      &:hover {
        transform: scale(1.15);
      }
    }
  }

  // ============================================
  // EMPTY STATE CARD
  // ============================================
  .empty-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%) !important;
    box-shadow: 0 12px 32px rgba(58, 103, 177, 0.12) !important;
    border: 2px dashed rgba(0, 151, 215, 0.2) !important;
    animation: slideInUp 0.6s ease-out;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      border-color: rgba(0, 151, 215, 0.4) !important;
    }

    .text-secondary {
      color: $text-90-black;
    }
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 960px) {
    padding: 20px 12px;

    .header-card {
      margin-bottom: 20px !important;
    }

    .header-title {
      font-size: 28px !important;
    }

    .header-subtitle {
      font-size: 13px !important;
    }
  }

  @media (max-width: 600px) {
    padding: 15px;

    .header-card {
      margin-bottom: 15px !important;
    }

    .header-title {
      font-size: 24px !important;
    }

    .header-subtitle {
      font-size: 12px !important;
    }

    .filters-card {
      .v-row {
        gap: 12px 0;
      }

      .v-col {
        margin-bottom: 12px;
      }
    }

    .table-card {
      .card-header {
        font-size: 14px;

        span {
          font-size: 14px !important;
        }
      }
    }

    .action-buttons {
      flex-wrap: wrap;
      gap: 4px;
    }
  }
}
</style>